import { Component, OnInit } from '@angular/core';
import { PagedResponse } from '@shared/models/paged-response';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Dealer } from '@shared/models/dealer';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { SummaryStatus } from '@shared/models/summary-status';
import { SpinnerService } from '@shared/services/spinner.service';
import { AuthService } from '@core/auth/auth.service';
import { ApiService } from '@shared/apis';
import { PageEvent } from '@shared/models/page-event';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import Utils from '@shared/utils/utils';
import { SummaryDate } from '@shared/models/summary-date';
import Configs from '@shared/constants/configs';
import DateUtils from '@shared/utils/date-utils';
import { Constants } from '@shared/constants/constants';
import { Company } from '@shared/enums/company';

@Component({
  selector: 'app-summary', templateUrl: './summary.component.html', styleUrls: ['./summary.component.scss']
})
export class SummaryComponent implements OnInit {
  view = 'STATUS';
  tab = 'DEALER';
  pagedResponse: PagedResponse;

  params: any = {};
  q: any = {};
  startDate = new FormControl(null);
  endDate = new FormControl(null);

  summaryStatusData!: SummaryStatus[];
  summaryDateData!: SummaryDate[];

  dealerSelectGroup!: Dealer[];

  bsModalRef: BsModalRef;


  bsConfig = {
    containerClass: 'theme-dk', showWeekNumbers: false, dateInputFormat: Configs.format.date,
  };
  startMaxDate: Date;
  afterDate = null;
  today: Date;

  userType: null;

  company;
  companies = Company;
  detailForm: FormGroup;

  constructor(
    private modalService: BsModalService,
    private spinnerService: SpinnerService,
    private authService: AuthService,
    private apiService: ApiService,
    protected fb: FormBuilder,
  ) {
    this.company = authService.getCurrentCompany();
  }

  ngOnInit(): void {
    this.getDealerList();
    this.setUserType();
    this.setFormControls();

    this.setDateRestriction();
    this.startDate.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(startDate => {
        this.showList();
      });
    this.endDate.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(endDate => {
        this.startMaxDate = endDate;
        if (this.startDate.value > endDate) {
          this.startDate.setValue(null);
        }
        this.showList();
      });
    this.showList();
  }

  setUserType() {
    const { userType } = this.authService.getToken();
    this.userType = userType;
    if (userType === Constants.userType.dealer) {
      this.tab = 'DEALER';
    } else if (userType === Constants.userType.agent) {
      this.tab = 'AGENT';
    }
  }

  setFormControls() {
    this.detailForm = this.fb.group({
      dealer: null,
    });
  }

  setDateRestriction() {
    this.today = DateUtils.getHqMidnight(new Date());
    this.startDate.setValue(DateUtils.addDays(this.today, -7));
    this.endDate.setValue(this.today);
    this.startMaxDate = this.today;
  }

  switchView(view) {
    this.view = view;
    this.showList();
  }

  switchTab(tab) {
    this.tab = tab;
    this.showList();
  }

  getDealerList() {
    this.apiService.dealer.getMyStores()
      .subscribe(res => {
        this.dealerSelectGroup = res;
      }, err => Utils.showError(err));
  }

  changeDealer(e) {
    if (e && e.bpNo) {
      this.params.dealerStore = e.code;
    } else {
      this.params.dealerStore = undefined;
    }
    this.showList();
  }

  showList(pageEvent?: PageEvent) {
    if (!this.startDate.value) {
      return;
    }
    if (!this.endDate.value) {
      return;
    }
    this.params.startDate = DateUtils.toHqMomentFormat(this.startDate.value, Configs.format.serverLocalDate);
    this.params.endDate = DateUtils.toHqMomentFormat(this.endDate.value, Configs.format.serverLocalDate);
    this.params.view = this.view;

    // TODO: Set Query with startDate, endDate, dealerStore, and view
    this.summaryStatusData = null;
    this.summaryDateData = null;
    this.spinnerService.start();

    if (this.tab === 'DEALER') {
      this.apiService.summary.getSummaryByDealer(this.params)
        .pipe(finalize(() => this.spinnerService.stop()))
        .subscribe(res => {
          if (this.view === 'STATUS') {
            this.summaryStatusData = res;
          } else {
            this.summaryDateData = res;
          }
        }, err => Utils.showError(err));
    } else {
      this.apiService.summary.getSummaryByAgent(this.params)
        .pipe(finalize(() => this.spinnerService.stop()))
        .subscribe(res => {
          if (this.view === 'STATUS') {
            this.summaryStatusData = res;
          } else {
            this.summaryDateData = res;
          }
        }, err => Utils.showError(err));
    }
  }

  reset() {
    this.setDateRestriction();
    this.changeDealer(null);
    this.detailForm.controls['dealer'].reset();
  }
}
